import i18n from '@/i18n/config';
import { useAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { changeContactMessageStatus, replyToContactMessage, updateContactMessage, deleteContactMessage } from '@/store';
import type { ContactMessageStatus } from '@/types';

export function useContactMessageActions() {
  const [, reply] = useAtom(replyToContactMessage);
  const [, update] = useAtom(updateContactMessage);
  const [, changeStatus] = useAtom(changeContactMessageStatus);
  const [, deleteMessage] = useAtom(deleteContactMessage);

  const [isReplying, setIsReplying] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleReply = useCallback(
    async ({ messageId, body, isNote }: { messageId: number; body: string; isNote?: boolean }) => {
      if (!body?.trim()) {
        toast.warning('Message body is required');
        return;
      }

      try {
        setIsReplying(true);
        await reply({ messageId, message: body, isNote });
        toast.success(isNote ? 'Note added' : 'Reply sent');
      } catch (error) {
        // handled globally
      } finally {
        setIsReplying(false);
      }
    },
    [reply]
  );

  const handleEdit = useCallback(
    async ({
      messageId,
      body,
      isNote,
      parentMessageId
    }: {
      messageId: number;
      body: string;
      isNote?: boolean;
      parentMessageId?: number;
    }) => {
      if (!body?.trim()) {
        toast.warning('Message body is required');
        return;
      }

      try {
        setIsEditing(true);
        await update({ messageId, message: body, isNote, parentMessageId });
        toast.success(i18n.t('copy.message_updated'));
      } catch (error) {
        // handled globally
      } finally {
        setIsEditing(false);
      }
    },
    [update]
  );

  const handleStatusChange = useCallback(
    async ({ messageId, status }: { messageId: number; status: ContactMessageStatus }) => {
      try {
        setIsUpdatingStatus(true);
        await changeStatus({ messageId, status });
        toast.success(i18n.t('copy.status_updated'));
      } catch (error) {
        // handled globally
      } finally {
        setIsUpdatingStatus(false);
      }
    },
    [changeStatus]
  );

  const handleDelete = useCallback(
    async ({ messageId }: { messageId: number }) => {
      try {
        setIsDeleting(true);
        await deleteMessage({ messageId });
        toast.success(i18n.t('copy.message_deleted'));
      } catch (error) {
        // handled globally
      } finally {
        setIsDeleting(false);
      }
    },
    [deleteMessage]
  );

  return {
    handleReply,
    handleEdit,
    handleStatusChange,
    handleDelete,
    isReplying,
    isEditing,
    isUpdatingStatus,
    isDeleting
  };
}

